/**
 * Vertical Menu Component
 */

import { IBuilderComponent } from '../../base'
import {
  formatColorFormat,
  formatComponentAlignmentFormat,
  formatResponsiveValue,
  formatSpacingFormat,
  formatTypographyFormat,
} from '../../format-utils'
import {
  deepMerge,
  parseAlignmentFormat,
  parseColorFormat,
  parseSpacingFormat,
  parseTypographyFormat,
} from '../../parser-utils'
import type {
  ColorAdaptiveValue,
  ColorVariant,
  ComponentAlignment,
  GradientDirection,
  ResponsiveValue,
  SpacingInputValue,
  TypographyValue,
} from '../../types'
import verticalMenuTemplate from './vertical-menu.template.json'

export interface IVerticalMenuComponentSettings {
  // Data settings
  'vertical-menu-data-source': string
  'vertical-menu-data-max-visible-items': ResponsiveValue<number>
  'vertical-menu-data-overflow-icon': string

  // Design settings
  'vertical-menu-design-typography': {
    'vertical-menu-design-title-typography': ResponsiveValue<TypographyValue>
    'vertical-menu-design-items-typography': ResponsiveValue<TypographyValue>
  }
  'vertical-menu-design-comp-margin': ResponsiveValue<SpacingInputValue>
  'vertical-menu-design-title-margin': ResponsiveValue<SpacingInputValue>
  'vertical-menu-design-items-margin': ResponsiveValue<SpacingInputValue>
  'vertical-menu-design-alignment': string
  'vertical-menu-design-items-alignment': string
  'vertical-menu-design-group-color': ColorAdaptiveValue<string>
  'vertical-menu-design-title-color': ColorAdaptiveValue<string>
  'vertical-menu-design-title-color-solid': ColorAdaptiveValue<ColorVariant>
  'vertical-menu-design-title-color-start-gradient': ColorAdaptiveValue<ColorVariant>
  'vertical-menu-design-title-color-end-gradient': ColorAdaptiveValue<ColorVariant>
  'vertical-menu-design-title-color-gradient-direction': ColorAdaptiveValue<GradientDirection>
  'vertical-menu-design-scheme': any
  'vertical-menu-design-items-color': ColorAdaptiveValue<string>
  'vertical-menu-design-items-color-solid': ColorAdaptiveValue<ColorVariant>
  'vertical-menu-design-items-color-start-gradient': ColorAdaptiveValue<ColorVariant>
  'vertical-menu-design-items-color-end-gradient': ColorAdaptiveValue<ColorVariant>
  'vertical-menu-design-items-color-gradient-direction': ColorAdaptiveValue<GradientDirection>
  'vertical-menu-design-orientation': string
  'vertical-menu-design-overflow-menu-background-color': ColorAdaptiveValue<string>
  'vertical-menu-design-overflow-menu-dropdown-align': ResponsiveValue<ComponentAlignment>
  'vertical-menu-design-show-icon': boolean
  'vertical-menu-design-show-title': boolean
}

export interface IVerticalMenuComponent extends IBuilderComponent {
  componentId: 'vertical-menu'
  groupId: 'app'
  settings: IVerticalMenuComponentSettings
}

/**
 * Vertical Menu AI Input
 */
export interface AiVerticalMenuInput {
  dataSource: string // Table ID
  maxVisibleItems?: number | string // Format: "50" or "50 ta:0 de:50"
  overflowIcon?: string // Icon name for overflow menu
  alignment?: string // Format: "start" or "center"
  itemsAlignment?: string // Format: "start" or "center"
  margin?: string // Format: "m:0px"
  titleMargin?: string // Format: "m:20px"
  itemsMargin?: string // Format: "m:10px"
  orientation?: 'horizontal' | 'vertical'
  overflowMenuBackgroundColor?: string // Format: "solid:primary-40" or "adaptive"
  overflowMenuDropdownAlign?: string // Format: "start" or "center"
  typography?: {
    title?: string // Title typography
    items?: string // Items typography
  }
  titleColor?: string // Format: "solid:primary-40" or "adaptive"
  itemsColor?: string // Format: "solid:primary-40" or "adaptive"
  showIcon?: boolean // Show icons
  showTitle?: boolean // Show title
}

const VERTICAL_MENU_DEFAULT_TEMPLATE = verticalMenuTemplate as unknown as IVerticalMenuComponentSettings

export class VerticalMenuComp {
  getTemplate(): IVerticalMenuComponentSettings {
    return VERTICAL_MENU_DEFAULT_TEMPLATE
  }

  /**
   * format vertical menu component settings to AI vertical menu input
   */
  format(settings: IVerticalMenuComponentSettings): AiVerticalMenuInput {
    const result: AiVerticalMenuInput = {
      dataSource: '',
    }

    // Data - Source
    if (settings['vertical-menu-data-source']) {
      result.dataSource = settings['vertical-menu-data-source']
    }

    // Data - Max Visible Items
    if (settings['vertical-menu-data-max-visible-items']) {
      const allSame = Object.values(settings['vertical-menu-data-max-visible-items']).every(
        (v) => v === Object.values(settings['vertical-menu-data-max-visible-items'])[0]
      )
      if (allSame) {
        result.maxVisibleItems = Object.values(settings['vertical-menu-data-max-visible-items'])[0]
      } else {
        result.maxVisibleItems = formatResponsiveValue(settings['vertical-menu-data-max-visible-items'], (val) =>
          String(val)
        )
      }
    }

    // Data - Overflow Icon
    if (settings['vertical-menu-data-overflow-icon']) {
      result.overflowIcon = settings['vertical-menu-data-overflow-icon']
    }

    // Design - Alignment
    if (settings['vertical-menu-design-alignment']) {
      result.alignment = settings['vertical-menu-design-alignment']
    }

    if (settings['vertical-menu-design-items-alignment']) {
      result.itemsAlignment = settings['vertical-menu-design-items-alignment']
    }

    // Design - Margin
    if (settings['vertical-menu-design-comp-margin']) {
      result.margin = formatSpacingFormat(settings['vertical-menu-design-comp-margin'])
    }

    if (settings['vertical-menu-design-title-margin']) {
      result.titleMargin = formatSpacingFormat(settings['vertical-menu-design-title-margin'])
    }

    if (settings['vertical-menu-design-items-margin']) {
      result.itemsMargin = formatSpacingFormat(settings['vertical-menu-design-items-margin'])
    }

    // Design - Orientation
    if (settings['vertical-menu-design-orientation']) {
      result.orientation = settings['vertical-menu-design-orientation'] as 'horizontal' | 'vertical'
    }

    // Design - Overflow Menu
    if (settings['vertical-menu-design-overflow-menu-background-color']) {
      result.overflowMenuBackgroundColor = formatColorFormat({
        colorType: settings['vertical-menu-design-overflow-menu-background-color'] as any,
      })
    }

    if (settings['vertical-menu-design-overflow-menu-dropdown-align']) {
      result.overflowMenuDropdownAlign =
        formatComponentAlignmentFormat(settings['vertical-menu-design-overflow-menu-dropdown-align'])?.replace(
          /^h:/,
          ''
        ) || undefined
    }

    // Design - Typography
    if (settings['vertical-menu-design-typography']) {
      result.typography = {}
      if (settings['vertical-menu-design-typography']['vertical-menu-design-title-typography']) {
        result.typography.title = formatTypographyFormat(
          settings['vertical-menu-design-typography']['vertical-menu-design-title-typography']
        )
      }
      if (settings['vertical-menu-design-typography']['vertical-menu-design-items-typography']) {
        result.typography.items = formatTypographyFormat(
          settings['vertical-menu-design-typography']['vertical-menu-design-items-typography']
        )
      }
    }

    // Design - Title Color
    if (settings['vertical-menu-design-title-color']) {
      result.titleColor = formatColorFormat({
        colorType: settings['vertical-menu-design-title-color'] as any,
        colorSolid: settings['vertical-menu-design-title-color-solid'],
        colorStartGradient: settings['vertical-menu-design-title-color-start-gradient'],
        colorEndGradient: settings['vertical-menu-design-title-color-end-gradient'],
        colorGradientDirection: settings['vertical-menu-design-title-color-gradient-direction'],
      })
    }

    // Design - Items Color
    if (settings['vertical-menu-design-items-color']) {
      result.itemsColor = formatColorFormat({
        colorType: settings['vertical-menu-design-items-color'] as any,
        colorSolid: settings['vertical-menu-design-items-color-solid'],
        colorStartGradient: settings['vertical-menu-design-items-color-start-gradient'],
        colorEndGradient: settings['vertical-menu-design-items-color-end-gradient'],
        colorGradientDirection: settings['vertical-menu-design-items-color-gradient-direction'],
      })
    }

    // Design - Show Icon/Title
    if (settings['vertical-menu-design-show-icon'] !== undefined) {
      result.showIcon = settings['vertical-menu-design-show-icon']
    }

    if (settings['vertical-menu-design-show-title'] !== undefined) {
      result.showTitle = settings['vertical-menu-design-show-title']
    }

    return result
  }

  /**
   * parse AI vertical menu input to vertical menu component settings
   */
  parse(input: AiVerticalMenuInput): IVerticalMenuComponentSettings {
    const result = deepMerge(VERTICAL_MENU_DEFAULT_TEMPLATE, {})

    // Data - Source (required)
    if (input.dataSource) {
      result['vertical-menu-data-source'] = input.dataSource
    }

    // Data - Max Visible Items
    if (input.maxVisibleItems !== undefined) {
      if (typeof input.maxVisibleItems === 'number') {
        result['vertical-menu-data-max-visible-items'] = {
          phone: input.maxVisibleItems,
          'phone-landscape': input.maxVisibleItems,
          tablet: input.maxVisibleItems,
          'tablet-landscape': input.maxVisibleItems,
          desktop: input.maxVisibleItems,
          tv: input.maxVisibleItems,
        }
      } else {
        // Parse responsive format
        const parts = input.maxVisibleItems.split(/\s+/)
        const itemsObj: ResponsiveValue<number> = {
          phone: 0,
          'phone-landscape': 50,
          tablet: 0,
          'tablet-landscape': 50,
          desktop: 50,
          tv: 50,
        }

        for (const part of parts) {
          if (part.includes(':')) {
            const [breakpoint, value] = part.split(':')
            const bpMap: Record<string, keyof ResponsiveValue<number>> = {
              p: 'phone',
              pl: 'phone-landscape',
              t: 'tablet',
              tl: 'tablet-landscape',
              de: 'desktop',
              tv: 'tv',
            }
            const bp = bpMap[breakpoint]
            if (bp) {
              itemsObj[bp] = parseInt(value) || 0
            }
          } else {
            const value = parseInt(input.maxVisibleItems) || 0
            Object.keys(itemsObj).forEach((key) => {
              itemsObj[key as keyof ResponsiveValue<number>] = value
            })
          }
        }

        result['vertical-menu-data-max-visible-items'] = itemsObj
      }
    }

    // Data - Overflow Icon
    if (input.overflowIcon) {
      result['vertical-menu-data-overflow-icon'] = input.overflowIcon
    }

    // Design - Alignment
    if (input.alignment) {
      result['vertical-menu-design-alignment'] = input.alignment
    }

    if (input.itemsAlignment) {
      result['vertical-menu-design-items-alignment'] = input.itemsAlignment
    }

    // Design - Margin
    if (input.margin) {
      result['vertical-menu-design-comp-margin'] = parseSpacingFormat(input.margin)
    }

    if (input.titleMargin) {
      result['vertical-menu-design-title-margin'] = parseSpacingFormat(input.titleMargin)
    }

    if (input.itemsMargin) {
      result['vertical-menu-design-items-margin'] = parseSpacingFormat(input.itemsMargin)
    }

    // Design - Orientation
    if (input.orientation) {
      result['vertical-menu-design-orientation'] = input.orientation
    }

    // Design - Overflow Menu
    if (input.overflowMenuBackgroundColor) {
      const colorParsed = parseColorFormat(input.overflowMenuBackgroundColor)
      result['vertical-menu-design-overflow-menu-background-color'] = colorParsed.colorType
    }

    if (input.overflowMenuDropdownAlign) {
      const alignmentParsed = parseAlignmentFormat(input.overflowMenuDropdownAlign)
      if (alignmentParsed.hAlign) {
        result['vertical-menu-design-overflow-menu-dropdown-align'] = alignmentParsed.hAlign
      }
    }

    // Design - Typography
    if (input.typography) {
      if (input.typography.title) {
        const titleTypo = parseTypographyFormat(input.typography.title)
        result['vertical-menu-design-typography']['vertical-menu-design-title-typography'] = titleTypo
      }

      if (input.typography.items) {
        const itemsTypo = parseTypographyFormat(input.typography.items)
        result['vertical-menu-design-typography']['vertical-menu-design-items-typography'] = itemsTypo
      }
    }

    // Design - Title Color
    if (input.titleColor) {
      const colorParsed = parseColorFormat(input.titleColor)
      result['vertical-menu-design-title-color'] = colorParsed.colorType
      result['vertical-menu-design-title-color-solid'] = colorParsed.colorSolid
      result['vertical-menu-design-title-color-start-gradient'] = colorParsed.colorStartGradient
      result['vertical-menu-design-title-color-end-gradient'] = colorParsed.colorEndGradient
      result['vertical-menu-design-title-color-gradient-direction'] = colorParsed.colorGradientDirection
    }

    // Design - Items Color
    if (input.itemsColor) {
      const colorParsed = parseColorFormat(input.itemsColor)
      result['vertical-menu-design-items-color'] = colorParsed.colorType
      result['vertical-menu-design-items-color-solid'] = colorParsed.colorSolid
      result['vertical-menu-design-items-color-start-gradient'] = colorParsed.colorStartGradient
      result['vertical-menu-design-items-color-end-gradient'] = colorParsed.colorEndGradient
      result['vertical-menu-design-items-color-gradient-direction'] = colorParsed.colorGradientDirection
    }

    // Design - Show Icon/Title
    if (input.showIcon !== undefined) {
      result['vertical-menu-design-show-icon'] = input.showIcon
    }

    if (input.showTitle !== undefined) {
      result['vertical-menu-design-show-title'] = input.showTitle
    }

    return result
  }
}
